import React, { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Instagram } from "lucide-react";
import { submitContact } from "../lib/api";
import { toast } from "sonner";
import { WA_CATALOG_URL, WA_PHONE_INTL, INSTAGRAM_URL, INSTAGRAM_HANDLE } from "../components/WhatsAppButton";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch { toast.error("Something went wrong"); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16" data-testid="contact-page">
      <p className="label-tiny text-brand-gold">Reach the Atelier</p>
      <h1 className="font-serif-brand text-4xl lg:text-5xl text-brand-charcoal mt-2">Let's talk jewellery</h1>
      <p className="text-brand-muted mt-3 max-w-xl">Our concierge team responds within 24 hours. For urgent styling help, WhatsApp us for instant support.</p>

      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 mt-14">
        <div className="space-y-6">
          {[
            { icon: Mail, l: "Email", v: "care@bindnijewels.com" },
            { icon: Phone, l: "Call Us", v: `${WA_PHONE_INTL} · Mon-Sat` },
            { icon: MessageCircle, l: "WhatsApp Catalogue", v: WA_PHONE_INTL, href: WA_CATALOG_URL },
            { icon: Instagram, l: "Instagram", v: INSTAGRAM_HANDLE, href: INSTAGRAM_URL },
            { icon: MapPin, l: "Atelier & Head Office", v: "10 B, Pushp Vihar Colony Extension, Indore, Madhya Pradesh 452010, India" },
          ].map((c) => (
            <div key={c.l} className="border border-brand-gold/30 p-5 bg-brand-ivory-soft flex gap-4">
              <c.icon size={20} strokeWidth={1.4} className="text-brand-maroon mt-1 shrink-0" />
              <div>
                <p className="label-tiny text-brand-gold">{c.l}</p>
                {c.href ? (
                  <a href={c.href} target="_blank" rel="noreferrer" className="text-brand-charcoal mt-1 hover:text-brand-maroon underline-offset-2 hover:underline">{c.v}</a>
                ) : (
                  <p className="text-brand-charcoal mt-1">{c.v}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="border border-brand-gold/30 bg-brand-ivory-soft p-8 space-y-5" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="label-tiny text-brand-muted block mb-2">Full Name</label>
              <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-brand-ivory border border-brand-gold/40 px-4 py-3 outline-none focus:border-brand-maroon" data-testid="contact-name" />
            </div>
            <div>
              <label className="label-tiny text-brand-muted block mb-2">Email</label>
              <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full bg-brand-ivory border border-brand-gold/40 px-4 py-3 outline-none focus:border-brand-maroon" data-testid="contact-email" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="label-tiny text-brand-muted block mb-2">Phone (optional)</label>
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full bg-brand-ivory border border-brand-gold/40 px-4 py-3 outline-none focus:border-brand-maroon" data-testid="contact-phone" />
            </div>
            <div>
              <label className="label-tiny text-brand-muted block mb-2">Subject</label>
              <input required value={form.subject} onChange={(e) => set("subject", e.target.value)} className="w-full bg-brand-ivory border border-brand-gold/40 px-4 py-3 outline-none focus:border-brand-maroon" data-testid="contact-subject" />
            </div>
          </div>
          <div>
            <label className="label-tiny text-brand-muted block mb-2">Message</label>
            <textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className="w-full bg-brand-ivory border border-brand-gold/40 px-4 py-3 outline-none focus:border-brand-maroon resize-none" data-testid="contact-message" />
          </div>
          <button disabled={loading} className="btn-primary w-full lg:w-auto" data-testid="contact-submit">{loading ? "Sending…" : "Send Message"}</button>
        </form>
      </div>
    </div>
  );
}
